import { Theme } from '@mui/material';
import { colorUtils } from '..';
import {
    Warning as WarningIcon,
    VpnKey as VpnKeyIcon,
    NetworkCheck as NetworkIcon,
    Folder as FolderIcon,
    Computer as ComputerIcon,
    BugReport as BugReportIcon,
    Person as PersonIcon,
    CheckCircle as CheckCircleIcon,
    Info as InfoIcon,
    Dns as DnsIcon,
    Link as LinkIcon,
    Security as SecurityIcon,
    ContentPaste as ContentPasteIcon,
    Construction as SystemIcon,
    EscalatorWarning as PlaceholderIcon
} from '@mui/icons-material';
import { TicketStatus, TimelineEventType, IOCType, VendorSeverity, SeverityLevel } from '../../types';

/**
 * 1. STATUS & PRIORITY COLOR HELPERS
 * Single source of truth for status and priority colors across all pages.
 */
export const getStatusColor = (status: string, theme: Theme): string => {
    switch (status) {
        case 'new':         return theme.palette.status.new;
        case 'assigned':    return theme.palette.status.assigned;
        case 'in_progress': return theme.palette.status.in_progress;
        case 'pending':     return theme.palette.status.pending;
        case 'resolved':    return theme.palette.status.resolved;
        case 'escalated':   return theme.palette.severity.critical;
        case 'handled':         return theme.palette.status.closed;
        case 'false_positive':  return theme.palette.text.tertiary;
        default:                return theme.palette.primary.main;
    }
};

export const getPriorityColor = (priority: string, theme: Theme): string => {
    switch (priority) {
        case 'low':      return theme.palette.severity.low;
        case 'medium':   return theme.palette.severity.medium;
        case 'high':     return theme.palette.severity.high;
        case 'critical': return theme.palette.severity.critical;
        default:         return theme.palette.text.secondary;
    }
};

/**
 * 2. STATUS MAPPING
 * Maps internal TicketStatus to UI labels and theme colors.
 */
export const getStatusMapping = (status: TicketStatus, theme: Theme) => {
    const labelMap: Record<TicketStatus, string> = {
        [TicketStatus.NEW]:            'new',
        [TicketStatus.ASSIGNED]:       'assigned',
        [TicketStatus.IN_PROGRESS]:    'in progress',
        [TicketStatus.PENDING]:        'pending',
        [TicketStatus.RESOLVED]:       'resolved',
        [TicketStatus.ESCALATED]:      'escalated',
        [TicketStatus.CLOSED]:         'closed',
        [TicketStatus.FALSE_POSITIVE]: 'false positive',
    };
    return {
        color: getStatusColor(status, theme),
        label: labelMap[status] ?? 'new',
    };
};

/**
 * 3. RISK & SEVERITY MAPPING
 * Maps SOCmate scores and Vendor levels to semantic colors.
 */
export const getRiskScoreStyles = (score: number, theme: Theme) => {
    if (score >= 80) return { color: theme.palette.severity.critical, label: 'CRITICAL' };
    if (score >= 60) return { color: theme.palette.severity.high, label: 'HIGH' };
    if (score >= 40) return { color: theme.palette.severity.medium, label: 'MEDIUM' };
    if (score >= 20) return { color: theme.palette.severity.low, label: 'LOW' };
    return { color: theme.palette.text.secondary, label: 'INFO' };
};

export const getSeverityStyles = (severity: VendorSeverity | SeverityLevel, theme: Theme) => {
    const level = severity.toLowerCase() as SeverityLevel;
    const map: Record<SeverityLevel, { color: string; bgColor: string; label: string }> = {
        critical: { color: theme.palette.severity.critical, bgColor: colorUtils.withOpacity(theme.palette.error.main, 0.1), label: 'CRITICAL' },
        high:     { color: theme.palette.severity.high, bgColor: colorUtils.withOpacity(theme.palette.warning.main, 0.1), label: 'HIGH' },
        medium:   { color: theme.palette.severity.medium, bgColor: colorUtils.withOpacity(theme.palette.warning.light, 0.1), label: 'MEDIUM' },
        low:      { color: theme.palette.severity.low, bgColor: colorUtils.withOpacity(theme.palette.success.main, 0.1), label: 'LOW' },
        informational:     { color: theme.palette.info.main, bgColor: colorUtils.withOpacity(theme.palette.info.main, 0.1), label: 'INFO' },
    };
    return map[level] || map.medium;
};

/**
 * 4. SEVERITY CHIP STYLE
 * Tinted background + subtle border — reads as data, not decoration.
 */
export const getSeverityChipSx = (color: string) => ({
    color,
    backgroundColor: colorUtils.withOpacity(color, 0.12),
    border: `1px solid ${colorUtils.withOpacity(color, 0.22)}`,
    fontWeight: 700,
    fontSize: '0.72rem',
    letterSpacing: '0.04em',
    borderRadius: 1,
    height: 22,
    '& .MuiChip-label': { px: 1 },
});

/**
 * 5. STATUS CHIP STYLE
 * Lighter tint than severity — secondary visual hierarchy.
 */
export const getStatusChipSx = (color: string) => ({
    color,
    backgroundColor: colorUtils.withOpacity(color, 0.1),
    border: `1px solid ${colorUtils.withOpacity(color, 0.18)}`,
    fontWeight: 600,
    fontSize: '0.70rem',
    letterSpacing: '0.04em',
    borderRadius: 1,
    height: 22,
    '& .MuiChip-label': { px: 1 },
});

/**
 * 6. ICON MAPPING
 * Centralizes all security icons so the UI remains consistent.
 */
export const getTimelineIcon = (type: TimelineEventType) => {
    const iconMap: Record<TimelineEventType, React.ElementType> = {
        [TimelineEventType.ALERT]: WarningIcon,
        [TimelineEventType.LOGIN]: VpnKeyIcon,
        [TimelineEventType.NETWORK]: NetworkIcon,
        [TimelineEventType.FILE]: FolderIcon,
        [TimelineEventType.PROCESS]: ComputerIcon,
        [TimelineEventType.MALWARE]: BugReportIcon,
        [TimelineEventType.ANALYST_NOTE]: PersonIcon,
        [TimelineEventType.SYSTEM]: SystemIcon,
        [TimelineEventType.REMEDIATION]: CheckCircleIcon,
    };
    return iconMap[type] || InfoIcon;

}

export const getIOCIcon = (type: IOCType) => {
    const iocMap: Record<IOCType, React.ElementType> = {
        [IOCType.IP]: DnsIcon,
        [IOCType.DOMAIN]: LinkIcon,
        [IOCType.URL]: LinkIcon,
        [IOCType.HASH_MD5]: SecurityIcon,
        [IOCType.HASH_SHA1]: SecurityIcon,
        [IOCType.HASH_SHA256]: SecurityIcon,
        [IOCType.FILE_NAME]: FolderIcon,
        [IOCType.FILE_PATH]: FolderIcon,
        [IOCType.REGISTRY_KEY]: PlaceholderIcon,
        [IOCType.EMAIL]: PersonIcon,
        [IOCType.USER_AGENT]: PlaceholderIcon,
    };
    return iocMap[type] || ContentPasteIcon;
};

